import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import {
  Box,
  CircularProgress,
  Divider,
  MenuItem,
  MenuList,
} from '@mui/material';
import { GridRowId, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';

import { KaseApi, KasePropertyApi } from '@/api';
import { PropertyEvidenceApi } from '@/api/api.evidence';
import { IcArrowDown, IcArrowUp } from '@/assets/images';
import { ConfirmationDialog, DataGrid, Page, PageHeader } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { formatSortParam } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import PropertyTypeRow from '@/features/properties/components/nested-row/PropertyTypeRow';
import { PropertyCustodyModal } from '@/features/properties/components/PropertyCustodyModal';
import { PropertyLabelPrint } from '@/features/properties/components/PropertyLabelPrint';
import {
  CustodyActions,
  ICustodyAction,
  IKasePropertyActionMenuItem,
  KasePropertyActionMemuFooterItems,
  KasePropertyActionMemuMainItems,
  KasePropertyEditors,
  KasePropertyViewers,
} from '@/features/properties/data';
import { useKasePropertyContext } from '@/features/properties/PropertyContext';
import { KasePropertyActionMenuRoot } from '@/features/properties/styles';
import { useFormConfigLoader, useKaseContext } from '@/hooks';
import { IKase, IOffenseOptionItem, IProperty, PropertyStatus } from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import { propertyEvidenceDataGridCols } from '@/pages/Properties/data';
import { hasAnyOfTargetRoles, isAccountAdmin } from '@/services';
import { useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { getLocalStorageItem, getMasterNameDisplayName } from '@/utils';

interface PropertyEvidenceDataGridProps {
  onSelectKaseId: (kaseId: string) => void;
}

const PropertyEvidenceDataGrid = ({
  onSelectKaseId,
}: PropertyEvidenceDataGridProps) => {
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const navigate = useNavigate();

  const {
    setCurrentPropertyId,
    setCustodyModalValues,
    setShouldRefreshProperties,
    shouldRefreshProperties,
    custodyModalValues,
  } = useKasePropertyContext();

  const [gridKey, setGridKey] = useState(0);

  const { updateKase } = useKaseContext();

  const apiRef = useGridApiRef();

  const { filteredOptions, loading } = useFormConfigLoader({
    optionsName: 'PROPERTY',
    category: ['DRUGS', 'GENERAL', 'PROPERTY'],
  });

  const drugTypesOptions = getOffenseOptionItems(
    'type',
    filteredOptions,
  ) as Map<string, IOffenseOptionItem>;

  const drugDataTypeOptions = getOffenseOptionItems(
    'dataSource',
    filteredOptions,
  ) as Map<string, IOffenseOptionItem>;

  const propertyType = getOffenseOptionItems(
    'propertyType',
    filteredOptions,
  ) as Map<string, IOffenseOptionItem>;

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    GridRowId[]
  >([]);

  const [menuState, setMenuState] = useState({
    anchorEl: null as HTMLElement | null,
    selectedRow: null as IProperty | null,
    propertyStatus: undefined as PropertyStatus | undefined,
    ownerName: undefined as string | undefined,
    rowAgencyId: '' as string,
    isEditor: false,
    isViewer: false,
  });

  const [showConfirm, setShowConfirm] = useState(false);

  // Print label state and ref
  const printLabelRef = useRef<HTMLDivElement>(null);
  const [printData, setPrintData] = useState<{
    property: {
      _id: string;
      number?: string;
      name?: string;
      typeTag?: string;
      involvement?: string;
      value?: number;
      description?: string;
      loggedInfo?: IProperty['loggedInfo'];
      createdBy?: IProperty['createdBy'];
      explosive?: {
        quantity?: number;
      };
      alcohol?: {
        quantity?: number;
        measurementType?: string;
      };
      drugs?: Array<{
        quantity?: number;
        measurementType?: string;
      }>;
    };
    cfsNumber?: string;
    propertyType?: string;
    kase?: any;
  } | null>(null);
  const [isPrintLoading, setIsPrintLoading] = useState(false);

  // Configure react-to-print
  const handlePrint = useReactToPrint({
    contentRef: printLabelRef,
    onAfterPrint: () => {
      setPrintData(null);
      setIsPrintLoading(false);
    },
  });

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `property-evidence-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return propertyEvidenceDataGridCols;
      }

      return mergeColumnSettings(propertyEvidenceDataGridCols, savedSettings);
    },
  );

  useEffect(() => {
    if (!shouldRefreshProperties) return;
    setGridKey((prev) => prev + 1);
    setShouldRefreshProperties(false);
  }, [shouldRefreshProperties, setShouldRefreshProperties]);

  const fetchPropertyList = useCallback(
    async (
      page: number,
      limit: number,
      filter: string,
      sort?: string,
    ): Promise<{ rows: IProperty[]; totalCount: number }> => {
      try {
        const response = await PropertyEvidenceApi.list({
          page,
          limit,
          filter,
          sort: formatSortParam(sort, { createdAt: -1 }),
        });

        const { data } = response;
        return { rows: data.docs, totalCount: data.totalDocs };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [updateToast],
  );

  const detailPanelExpandedRowIdsChangeHandler = useCallback(
    (newIds: GridRowId[]) => {
      setDetailPanelExpandedRowIds(
        newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds,
      );
    },
    [],
  );

  const rowClickHandler = useCallback((params: GridRowParams, event: any) => {
    const target = event?.target as HTMLElement | null;

    // Ignore interactive elements inside the row (actions, links, buttons).
    if (
      target?.closest('button, a, [role="button"], .MuiDataGrid-actionsCell')
    ) {
      return;
    }

    setDetailPanelExpandedRowIds((prev) =>
      prev[0] === params.id ? [] : [params.id],
    );
  }, []);

  // Get property type label helper
  const getPropertyTypeLabel = useCallback(
    (typeKey?: string) => {
      if (!typeKey || !propertyType?.size) return undefined;
      const allPropertyTypes = Array.from(propertyType.values());
      return allPropertyTypes.find((value) => value.key === typeKey)?.label;
    },
    [propertyType],
  );

  // Handle print label - fetches data and triggers print
  const handlePrintPropertyLabel = useCallback(
    async (property: IProperty) => {
      // Validate property has required _id
      if (!property?._id) {
        updateToast({ open: true, message: 'Property ID is missing' });
        return;
      }

      // Handle both string and object types for kase
      let kaseId: string | undefined;
      if (typeof property.kase === 'string') {
        kaseId = property.kase;
      } else if (
        property.kase &&
        typeof property.kase === 'object' &&
        '_id' in property.kase
      ) {
        kaseId = (property.kase as { _id?: string })._id;
      }

      if (!kaseId) {
        updateToast({ open: true, message: 'Invalid Case ID' });
        return;
      }

      setIsPrintLoading(true);

      try {
        // Fetch CFS links
        let cfsNumber: string | undefined;
        try {
          const cfsResponse = await KaseApi.getLinkedCFS(kaseId);
          const cfsData =
            (cfsResponse?.data as any)?.results || cfsResponse?.data;
          cfsNumber = Array.isArray(cfsData)
            ? cfsData[0]?.cfs?.number
            : cfsData?.cfs?.number;
        } catch {
          // CFS is optional, continue without it
          cfsNumber = undefined;
        }

        // Get property type label
        const propertyTypeLabel = getPropertyTypeLabel(property.type);

        // Create properly typed object for printing
        setPrintData({
          property: {
            _id: property._id,
            number: property.number,
            name: property.name,
            typeTag: property.typeTag,
            involvement: property.involvement,
            value: property.value,
            description: property.description,
            loggedInfo: property.loggedInfo,
            createdBy: property.createdBy,
            explosive: property.explosive
              ? {
                  quantity: property.explosive.quantity ?? undefined,
                }
              : undefined,
            alcohol: property.alcohol
              ? {
                  quantity: property.alcohol.quantity ?? undefined,
                  measurementType:
                    property.alcohol.measurementType ?? undefined,
                }
              : undefined,
            drugs:
              property.drugs?.map((drug) => ({
                quantity: drug.quantity ?? undefined,
                measurementType: drug.measurementType ?? undefined,
              })) ?? undefined,
          },
          cfsNumber,
          propertyType: propertyTypeLabel,
          kase:
            typeof property.kase === 'string'
              ? { number: '' } // Fallback if kase is just a string
              : property.kase,
        });

        // Trigger print after component renders
        setTimeout(() => {
          handlePrint();
        }, 100);
      } catch (error: any) {
        setIsPrintLoading(false);
        updateToast({
          open: true,
          message: error.message || 'Failed to print label',
        });
      }
    },
    [handlePrint, updateToast, getPropertyTypeLabel],
  );

  const handleMenuClose = useCallback(() => {
    setMenuState({
      anchorEl: null,
      selectedRow: null,
      propertyStatus: undefined,
      ownerName: undefined,
      rowAgencyId: '',
      isEditor: false,
      isViewer: false,
    });
  }, []);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, row: IProperty) => {
      event.stopPropagation();

      if (custodyModalValues.open || showConfirm) return;

      const kaseId =
        typeof row.kase === 'string' ? row.kase : (row.kase as any)?._id;
      const agencyId =
        typeof row.kase === 'string'
          ? ''
          : (row.kase as any)?.agency?._id || '';

      if (!kaseId) {
        updateToast({ open: true, message: 'Invalid Case ID' });
        return;
      }

      const isEditor = user
        ? isAccountAdmin(user) ||
          (!!agencyId &&
            hasAnyOfTargetRoles(user, KasePropertyEditors, agencyId))
        : false;
      const isViewer =
        isEditor ||
        (!!user &&
          !!agencyId &&
          hasAnyOfTargetRoles(user, KasePropertyViewers, agencyId));

      const ownerName = row.owner
        ? getMasterNameDisplayName(row.owner)
        : undefined;

      if (row.kase && typeof row.kase === 'object') {
        updateKase(row.kase as IKase);
      }

      onSelectKaseId(kaseId);
      setCurrentPropertyId(row._id);
      setMenuState({
        anchorEl: event.currentTarget,
        selectedRow: row,
        propertyStatus: row.status,
        ownerName,
        rowAgencyId: agencyId,
        isEditor,
        isViewer,
      });
    },
    [
      custodyModalValues.open,
      showConfirm,
      user,
      onSelectKaseId,
      setCurrentPropertyId,
      updateKase,
      updateToast,
    ],
  );

  const goToKaseProperty = useCallback(
    (screen: 'view' | 'edit') => {
      const row = menuState.selectedRow;
      if (!row) return;
      const kaseId =
        typeof row.kase === 'string' ? row.kase : (row.kase as any)?._id;
      if (!kaseId || !row._id) return;

      const url = `/cases/${kaseId}/properties?propertyId=${row._id}${
        screen === 'edit' ? '&screen=edit' : ''
      }`;
      navigate(url);
      handleMenuClose();
    },
    [menuState.selectedRow, navigate, handleMenuClose],
  );

  const handleDelete = useCallback(async () => {
    const row = menuState.selectedRow;
    if (!row?._id) return;
    const kaseId =
      typeof row.kase === 'string' ? row.kase : (row.kase as any)?._id;
    if (!kaseId) {
      updateToast({ open: true, message: 'Invalid Case ID' });
      return;
    }

    try {
      await KasePropertyApi.delete(kaseId, row._id);
      setShouldRefreshProperties(true);
      setShowConfirm(false);
      handleMenuClose();
    } catch (e: any) {
      updateToast({
        open: true,
        message: e?.message || 'Cannot delete property.',
      });
    }
  }, [
    menuState.selectedRow,
    setShouldRefreshProperties,
    handleMenuClose,
    updateToast,
  ]);

  const handleMenuItemClick = useCallback(
    (custodyAction?: ICustodyAction) => {
      if (custodyAction === CustodyActions.PRINT_LABEL) {
        const row = menuState.selectedRow;
        handleMenuClose();
        if (row) handlePrintPropertyLabel(row);
        return;
      }

      setCustodyModalValues({
        open: true,
        custodyAction,
        propertyOwnerName: menuState.ownerName,
      });
      handleMenuClose();
    },
    [
      menuState.selectedRow,
      menuState.ownerName,
      setCustodyModalValues,
      handleMenuClose,
      handlePrintPropertyLabel,
    ],
  );

  const renderMenuItems = (menuItem: IKasePropertyActionMenuItem) => {
    const propertyStatus = menuState.propertyStatus;
    if (
      propertyStatus &&
      menuItem.allowedPropertyStatus &&
      !menuItem.allowedPropertyStatus.includes(propertyStatus)
    ) {
      return null;
    }

    if (!menuState.anchorEl) return null;

    if (menuItem.subMenuItems) {
      return (
        <label
          key={menuItem.id}
          className="expander-label"
          htmlFor={menuItem.id}
        >
          <input type="checkbox" id={menuItem.id} />
          <MenuItem>
            {menuItem.label}
            <IcArrowDown className="icDown" />
            <IcArrowUp className="icUp" />
          </MenuItem>
          <MenuList className="sub-menu-list">
            {menuItem.subMenuItems.map((subItem) => renderMenuItems(subItem))}
          </MenuList>
        </label>
      );
    }

    return (
      <MenuItem
        key={menuItem.id}
        onClick={() => handleMenuItemClick(menuItem.value)}
      >
        {menuItem.label}
      </MenuItem>
    );
  };

  const columns = useDataGridColumns({
    type: 'propertyEvidence',
    columnSettings,
    navigate,
    filteredOptions,
    actionMethods: {
      openMenu: handleMenuOpen,
    },
  });

  if (loading) {
    return null;
  }

  return (
    <Page title="Evidence">
      <PageHeader title="Evidence" sx={{ borderBottom: 'none' }} />
      <DataGrid
        name="property-evidence"
        key={gridKey}
        apiRef={apiRef}
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        fetchData={fetchPropertyList}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        onDetailPanelExpandedRowIdsChange={
          detailPanelExpandedRowIdsChangeHandler
        }
        onRowClick={rowClickHandler}
        detailPanelContent={
          columnSettings.every((col) => {
            return col.visible === false;
          })
            ? undefined
            : ({ row }) => {
                const processedRow = row;
                if (
                  row?.drugs?.length &&
                  drugTypesOptions &&
                  drugDataTypeOptions
                ) {
                  processedRow.drugs = row.drugs.map((drugItem: any) => {
                    let drugTypeLabels, drugDataLabels;
                    const drugItemType = drugTypesOptions.get(drugItem.type);
                    const drugItemDataSource = drugDataTypeOptions.size
                      ? drugDataTypeOptions.get(drugItem.dataSource?.toString())
                      : '';
                    if (drugItemType) {
                      drugTypeLabels = {
                        type: drugItemType?.label,
                      };
                    }
                    if (drugItemDataSource) {
                      drugDataLabels = {
                        dataSource: drugItemDataSource?.label,
                      };
                    }
                    return {
                      ...drugItem,
                      ...drugTypeLabels,
                      ...drugDataLabels,
                    };
                  });
                }

                return (
                  <PropertyTypeRow
                    row={processedRow}
                    propertyType={propertyType}
                    paddingLeft={7.5}
                  />
                );
              }
        }
        sx={{
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(238, 62, 92, 0.4)',
          },
          '& .MuiCheckbox-root[aria-disabled="true"]': {
            display: 'none',
          },
        }}
        hiddenColumnCount={1}
      />

      {isPrintLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      {/* Hidden print component - only renders when printData is set */}
      {printData && printData.kase && (
        <div style={{ display: 'none' }}>
          <PropertyLabelPrint
            ref={printLabelRef}
            kase={printData.kase}
            propertyDetails={printData.property}
            cfsNumber={printData.cfsNumber}
            propertyType={printData.propertyType}
          />
        </div>
      )}

      <KasePropertyActionMenuRoot
        id="property-evidence-actions-menu"
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={handleMenuClose}
      >
        {menuState.isViewer && (
          <MenuItem onClick={() => goToKaseProperty('view')}>View</MenuItem>
        )}
        {menuState.isEditor && [
          <MenuItem key="edit" onClick={() => goToKaseProperty('edit')}>
            Edit
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => setShowConfirm(true)}
            sx={{ color: colors.error.main }}
          >
            Delete
          </MenuItem>,
          ...KasePropertyActionMemuMainItems.map((actionMenuItem) =>
            renderMenuItems(actionMenuItem),
          ),
          <Divider key="div" />,
        ]}
        {menuState.isEditor &&
          KasePropertyActionMemuFooterItems.map((actionMenuItem) =>
            renderMenuItems(actionMenuItem),
          )}
      </KasePropertyActionMenuRoot>

      <PropertyCustodyModal />

      <ConfirmationDialog
        title="Delete Property?"
        description="Warning! You are about to delete this property, are you sure?"
        confirmText="Delete"
        open={showConfirm}
        onCancel={() => {
          setShowConfirm(false);
          handleMenuClose();
        }}
        onConfirm={handleDelete}
      />
    </Page>
  );
};

export default PropertyEvidenceDataGrid;
